import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import type { SizingLines, SizingTemplate } from '../utils/sizingEstimate';
import { defaultHoursEach, sizeLine } from '../utils/sizingEstimate';

/**
 * The lines of a template's estimate, each editable three ways: in or out of the estimate,
 * how many, and how many hours each (pre-filled with the item's own estimate, or the category
 * default when the template was sized from its config items). Shared by Sell › Sizing and the
 * SOW generator so both read and edit the same numbers.
 */
interface SizingLinesEditorProps {
  template: SizingTemplate;
  lines: SizingLines;
  onChange: (id: string, patch: Partial<SizingLines[string]>) => void;
  compact?: boolean;
}

export function SizingLinesEditor({ template, lines, onChange, compact = false }: SizingLinesEditorProps) {
  return (
    <div className={compact ? 'grid grid-cols-1 md:grid-cols-2 gap-2' : 'space-y-2'} data-testid="sizing-items">
      {template.scopeItems.map((item) => {
        const line = lines[item.id] ?? { quantity: item.defaultValue, hoursEach: '', included: true };
        const sized = sizeLine(item, line.quantity, line.hoursEach);
        const fromTemplate = item.id.startsWith('category:');
        const templateCount = typeof item.defaultValue === 'number' ? item.defaultValue : Number(item.defaultValue) || 0;
        return (
          <div key={item.id} className={`grid grid-cols-[auto_1fr_96px_96px_auto] items-center gap-3 rounded-md border px-3 py-2 ${line.included ? '' : 'opacity-60'}`} data-testid={`sizing-line-${item.id}`}>
            <Checkbox checked={line.included} aria-label={`Include ${item.name}`} onCheckedChange={(v) => onChange(item.id, { included: v === true })} />
            <div className="min-w-0">
              <div className={`font-medium truncate ${compact ? 'text-sm' : ''}`}>{item.name}{item.required ? '' : <span className="text-xs text-muted-foreground"> · optional</span>}</div>
              {!compact && item.description && !fromTemplate && <div className="text-xs text-muted-foreground">{item.description}</div>}
              <div className="text-xs text-muted-foreground" data-testid={`sizing-line-summary-${item.id}`}>
                {line.included
                  ? `${sized.quantity} ${sized.quantity === 1 ? 'item' : 'items'}${fromTemplate ? ` (${templateCount} in the template)` : ''} × ${sized.quantity > 0 ? Math.round((sized.hoursEstimate / sized.quantity) * 100) / 100 : 0} h = ${Math.round(sized.hoursEstimate)} h`
                  : 'Not in this estimate'}
              </div>
            </div>
            <label className="text-xs text-muted-foreground grid gap-0.5">
              <span>Items</span>
              <Input type="number" min={0} aria-label={`Quantity of ${item.name}`} value={line.quantity} disabled={!line.included}
                onChange={(e) => onChange(item.id, { quantity: e.target.value === '' ? '' : Number(e.target.value) })} />
            </label>
            <label className="text-xs text-muted-foreground grid gap-0.5">
              <span>Hours each</span>
              <Input type="number" min={0} step={0.5} aria-label={`Hours each for ${item.name}`} value={line.hoursEach} placeholder={String(defaultHoursEach(item))} disabled={!line.included}
                onChange={(e) => onChange(item.id, { hoursEach: e.target.value === '' ? '' : Number(e.target.value) })} />
            </label>
            <div className="text-sm tabular-nums text-right min-w-[4ch]" aria-label={`Hours for ${item.name}`}>{line.included ? `${Math.round(sized.hoursEstimate)} h` : '—'}</div>
          </div>
        );
      })}
    </div>
  );
}
